import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useBusinessNow } from '../../lib/business-time.ts';
import { formatLongDate } from '../../lib/format.ts';
import { usePublishPageTitle, useScreen } from '../../lib/layout.ts';
import { RecordMenu, type RecordMenuItem } from '../record-head.tsx';
import { Button } from './button.tsx';
import { HelpHint } from './help-hint.tsx';
import { PhoneActionBar } from './phone-action-bar.tsx';
import { buttonClass, pageHeadSplit } from './variants.ts';
import { cn } from '../../lib/utils.ts';

export interface PageHeadAction {
  label: string;
  onSelect: () => void;
  disabled?: boolean;
  /** Test-Kennung des Knopfs (Ticket 1.245; z. B. „bestellung-einlesen“). */
  testId?: string;
  /** Tooltip, z. B. „kommt mit Story 11.9“ bei einem ausgegrauten Knopf (Ticket 1.249). */
  hint?: string;
  /**
   * Ziel als Link (Ticket 1.247): im Kopf dann ein Link im Knopf-Aussehen (neuer Tab, Mittelklick, `href`); unter „⋯“
   * gilt weiter `onSelect`. Standard: Knopf.
   */
  to?: string;
}

/**
 * Der eine Seitenkopf (DESIGN „Seitenkopf“, Ticket 1.271; vorher `PageHeader` in app-shell und `PageHead`): Pfadzeile
 * „Label · Datum“ `text-body` `ink-3`, darunter mit `space-1` Abstand der Seitentitel `text-page-title`/500 (Zeilenhöhe
 * 1,15, −0,01 em; Kompakt 22 px über den Token). Daneben optional Kurzzeile (`summary`), „?“ (`intro`) und Reiter (`nav`,
 * Einstellungen). Rechts höchstens 3 Knöpfe plus „⋯“ (Hauptknopf zuerst): `primaryAction`, Zweitknöpfe `actions`
 * (über dem Limit als Einträge vor das übrige „⋯“-Menü `menu`), Link-Chips `chips` und frei gesetzte Elemente `extra`
 * (Umschalter, Kopf-Links) zählen nicht mit. Am Handy steht der Hauptknopf unten fixiert (`PhoneActionBar`), der Titel
 * zusätzlich in der Kopfzeile (`usePublishPageTitle`). Seiten setzen keine eigenen Werte; Datensätze nutzen
 * `HiddenPageTitle` und den Datensatz-Kopf.
 */
export function PageHeader({
  title,
  label,
  trail,
  trailTestId,
  nav,
  intro,
  icon,
  summary,
  titleTestId,
  primaryAction,
  actions = [],
  menu = [],
  chips,
  extra,
  phoneBarFramed = false,
  phoneTitleInBar = false,
  phoneMetaRow = false,
}: {
  title: string;
  /** Erster Teil der Pfadzeile vor dem Datum (z. B. „Verkauf“). */
  label?: string | undefined;
  /**
   * Unterpunkt hinter „›“ im selben Titel (Ticket 1.258, DESIGN „Reiter der Einstellungen“): „Einstellungen › Verkauf“.
   * Der Kopf meldet den vollen Titel an (Handy-Kopfzeile).
   */
  trail?: string | undefined;
  /** `data-testid` des Unterpunkts (z. B. `einstellungen-hauptpunkt`). */
  trailTestId?: string | undefined;
  /** Inhalt neben bzw. unter der Titelzeile, linksbündig (z. B. die Reiter der Einstellungen als Segment). */
  nav?: ReactNode;
  /** Erklärung der Seite: steht hinter dem „?“ (`HelpHint`, „Hilfe zu ‹Titel›“) direkt hinter dem Titel, nie als Absatz. */
  intro?: string | undefined;
  /** Symbol vor dem Titel (Story 1.251, z. B. Ablauf-Symbol der Automatik); rein schmückend (`aria-hidden`). */
  icon?: ReactNode;
  /** Kurzzeile neben dem Titel; `null`/leer = keine (z. B. solange die Zahl lädt). */
  summary?: ReactNode;
  /** `data-testid` der Überschrift (für Seiten, deren Titel sich nach dem Speichern ändert). */
  titleTestId?: string | undefined;
  /** Genau ein Hauptknopf (`Button` primary) oder keiner. */
  primaryAction?: ReactNode;
  /** Zweitknöpfe in Reihenfolge. */
  actions?: readonly PageHeadAction[];
  /** Seltenes unter „⋯“. */
  menu?: readonly RecordMenuItem[];
  /**
   * Link-Chips vor den Knöpfen (Ticket 1.245, opt-in; Entwurf C01 „Auftrags-Entwürfe · Rahmenaufträge“). Zählen nicht zu
   * den 3 Knöpfen – sie führen zu Nachbarseiten, statt etwas auszulösen.
   */
  chips?: ReactNode;
  /** Opt-in (Ticket 1.273): Handy-Knopfleiste mit eigener Fläche (Rahmenlinie, Safe-Area-Polster), siehe `FixedActionBar`. */
  phoneBarFramed?: boolean;
  /**
   * Opt-in (Story 5.120, Clean): am Handy steht der Titel nur in der Kopfzeile der App – die Überschrift bleibt für
   * Hilfsmittel als `sr-only` erhalten.
   */
  phoneTitleInBar?: boolean;
  /**
   * Opt-in (Story 9.41, Listen-Köpfe): mit `phoneTitleInBar` stehen am Handy Datum und „⋯“ in einer Zeile statt „⋯“
   * allein in einer Reihe. Nur für Köpfe ohne Kurzzeile und breite Inhalte – sonst wird der Kopf breiter als der Schirm.
   */
  phoneMetaRow?: boolean;
  /** Frei gesetzte Elemente rechts im Kopf (Umschalter, `HeaderLink`); nur mit echtem Ziel. Zählen nicht zu den 3 Knöpfen. */
  extra?: ReactNode;
}) {
  // Fachliches Datum: mit gestellter Testuhr deren Datum.
  const { now } = useBusinessNow();
  usePublishPageTitle(trail ? `${title} › ${trail}` : title);
  const phone = useScreen() === 'phone';
  const inlinePrimary = Boolean(primaryAction) && !phone;
  const { shown, overflow } = pageHeadSplit(inlinePrimary ? 1 : 0, actions);
  const menuItems: RecordMenuItem[] = [...overflow.map((a) => ({ label: a.label, onSelect: a.onSelect, disabled: a.disabled, reason: a.hint })), ...menu];
  const hasButtons = Boolean(extra) || Boolean(chips) || inlinePrimary || shown.length > 0 || menuItems.length > 0;

  const heading = trail ? (
    <>
      {title} <span className="text-ink-3">›</span> <span data-testid={trailTestId}>{trail}</span>
    </>
  ) : (
    title
  );
  // Erklärung der Seite hinter dem „?“ direkt hinter dem Titel statt als Absatz darunter (Ticket 1.157, DESIGN „Hilfe „?““).
  const h1 = (
    <h1
      className={phone && phoneTitleInBar ? 'sr-only' : 'text-page-title leading-[1.15] font-medium tracking-[-0.01em] [overflow-wrap:anywhere]'}
      data-testid={titleTestId}
      data-page-title=""
    >
      {heading}
    </h1>
  );
  const titleLine = intro ? (
    <div className="flex min-w-0 items-center self-start sm:self-auto" data-page-title-line="">
      {h1}
      <HelpHint label={title} text={intro} />
    </div>
  ) : (
    h1
  );
  return (
    <>
      <header
        className={cn(
          'mb-[var(--page-head-gap)] flex min-w-0 flex-col items-stretch gap-y-space-3 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between sm:gap-x-space-6',
          phone && phoneTitleInBar && phoneMetaRow && 'flex-row items-center justify-between gap-x-space-3',
        )}
      >
        <div className="flex min-w-0 flex-col gap-space-1">
          <div className="type-meta flex flex-wrap gap-x-space-2 text-ink-3" data-page-path="">
            {label ? (
              <>
                <span className="type-label text-ink-2">{label}</span>
                <span aria-hidden>·</span>
              </>
            ) : null}
            <span>{formatLongDate(now)}</span>
          </div>
          <div className="flex min-w-0 flex-col gap-y-space-1 sm:flex-row sm:flex-wrap sm:items-baseline sm:gap-x-space-4">
            {icon ? (
              <div className="flex min-w-0 items-center gap-space-3 self-start sm:self-auto">
                <span aria-hidden className="flex shrink-0 items-center" data-page-icon="">
                  {icon}
                </span>
                {titleLine}
              </div>
            ) : (
              titleLine
            )}
            {summary ? (
              <p className="type-meta m-0 min-w-0 text-ink-3 tabular-nums [overflow-wrap:anywhere]" data-page-summary="">
                {summary}
              </p>
            ) : null}
            {nav ? (
              <div className="flex min-w-0 max-w-full sm:self-center" data-page-nav="">
                {nav}
              </div>
            ) : null}
          </div>
        </div>
        {hasButtons ? (
          <div className="flex min-w-0 max-w-full flex-wrap items-center gap-space-2" data-page-actions="">
            {extra}
            {chips}
            {inlinePrimary ? primaryAction : null}
            {shown.map((a) =>
              a.to && !a.disabled ? (
                <Link key={a.label} to={a.to} className={buttonClass('secondary')} title={a.hint} data-testid={a.testId}>
                  {a.label}
                </Link>
              ) : (
                <Button key={a.label} variant="secondary" disabled={a.disabled} title={a.hint} onClick={a.onSelect} data-testid={a.testId}>
                  {a.label}
                </Button>
              ),
            )}
            {menuItems.length > 0 ? <RecordMenu label="Weitere Aktionen" items={menuItems} /> : null}
          </div>
        ) : null}
      </header>
      {primaryAction && phone ? <PhoneActionBar framed={phoneBarFramed}>{primaryAction}</PhoneActionBar> : null}
    </>
  );
}
